import { useEffect, useMemo, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { twMerge } from "tailwind-merge";
import { useIntersectionObserver } from "usehooks-ts";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { faCaretUp } from "@fortawesome/free-solid-svg-icons/faCaretUp";

import { Loader } from "components/customElements";
import EmptyState from "components/EmptyState";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import Table, { Column } from "components/Table";

const COL_COLORS = [
  "bg-[#FFFFFF]",
  "bg-[#D8EEDC]",
  "bg-[#8BCC97]",
  "bg-[#97D1A0]",
  "bg-[#74C282]",
  "bg-[#4DB364]",
];

export const NumberCell = ({
  value,
  isLastCol,
}: {
  value: number;
  isLastCol?: boolean;
}) => {
  return (
    <div
      className={twMerge(
        "w-full font-bold p-[10px] py-[8px] text-center",
        !isLastCol && "border-r border-r-slate-200",
        value && COL_COLORS[Math.floor(value) == 0 ? 1 : Math.floor(value)],
        !value && COL_COLORS[0]
      )}
    >
      {value || "-"}
    </div>
  );
};

export const Cell = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={twMerge("w-full p-[10px] py-[8px] truncate", className)}>
      {children}
    </div>
  );
};

export const Header = (name: string, sortKey: string, activeSort: string) => {
  return (
    <div className="flex justify-between select-none">
      {name}
      {activeSort.includes(sortKey) && (
        <FontAwesomeIcon
          icon={!activeSort.includes("-") ? faCaretUp : faCaretDown}
        />
      )}
    </div>
  );
};

export default function ToolTable<T>({
  tableColumns,
  swr,
  onRowClick,
  rowClassName,
  containerClassName,
  rowClassNameCallback,
  cellClassNameCallback,
  afterRowRenderCallback,
  dataCallback,
  hasMoreCallback,
  allowLoadMore = true,
  headCellClassName,
}: {
  tableColumns: Column<any>[];
  swr: any;
  onRowClick?: (record: any, e: React.MouseEvent) => void;
  rowClassName?: string;
  headCellClassName?: string;
  rowClassNameCallback?: (record: any) => string;
  cellClassNameCallback?: (record: any, column: any) => string;
  afterRowRenderCallback?: (record: any, rowIndex: number) => React.ReactNode;
  dataCallback?: (data: any[]) => any[];
  hasMoreCallback?: (data: any[]) => boolean;
  allowLoadMore?: boolean;
  containerClassName?: string;
}) {
  const scrollableRef = useRef<HTMLDivElement>(null);

  const { data, isValidating, mutate, size, setSize } = swr;
  const hasMore = hasMoreCallback
    ? hasMoreCallback(data)
    : data?.[0]?.meta?.totalPages > size;

  const tableData = useMemo(() => {
    let rs = data?.reduce(
      (rs: any[], items: { data: any[] }, arrIndex: number) => {
        const pos = arrIndex * 10;

        return rs.concat(
          items?.data?.map?.((item: any, index: any) => ({
            number: pos + index + 1,
            ...item,
          })) || []
        );
      },
      []
    );

    if (dataCallback) {
      rs = dataCallback(rs);
    }

    return rs;
  }, [data, dataCallback]);

  const endRef = useRef<HTMLDivElement>(null);
  const endRefObserver = useIntersectionObserver(endRef, {});

  useEffect(() => {
    if (
      endRefObserver?.isIntersecting &&
      !isValidating &&
      hasMore &&
      allowLoadMore
    ) {
      setSize(size + 1);
    }
  }, [
    allowLoadMore,
    endRefObserver?.isIntersecting,
    hasMore,
    isValidating,
    setSize,
    size,
  ]);

  return (
    <div className="w-full overflow-scroll">
      <InfiniteScroll
        dataLength={data?.[0]?.meta?.recordCount || 0}
        next={() => {}}
        style={{ overflow: "visible" }}
        hasMore={hasMore}
        loader={null}
        pullDownToRefresh={false}
        releaseToRefreshContent={false}
        scrollThreshold={0.8}
      >
        <div
          className={twMerge(
            "mb-[30px] min-w-[1024px] overflow-x-auto i",
            containerClassName
          )}
        >
          <Table
            tableClassName="!border-0"
            headRowClassName="!border-0 !select-none !bg-white !p-[10px] !border-0 !border-b !border-b-slate-200 !border-solid"
            headCellClassName={twMerge(
              "!border-0 !select-none !bg-white !p-[10px] !border-0 !border-b !border-b-slate-200 !border-solid",
              headCellClassName
            )}
            rowClassName={twMerge(
              "border-0 border-b border-b-slate-200 border-solid",
              rowClassName
            )}
            rowClassNameCallback={rowClassNameCallback}
            cellClassNameCallback={cellClassNameCallback}
            afterRowRenderCallback={afterRowRenderCallback}
            cellClassName="!border-0 !p-0 !bg-white !align-middle"
            columns={tableColumns}
            dataSource={tableData || []}
            onRowClick={onRowClick}
          />
          {!isValidating && (!tableData || tableData?.length < 1) && (
            <EmptyState type="data" />
          )}
          {isValidating && <Loader />}
        </div>
      </InfiniteScroll>
      <div ref={endRef} className="h-[2px] w-full"></div>
    </div>
  );
}
